<template>
  <article v-if="!isLoading && article !== null" class="article">
    <div class="columns has-margin-bottom-md">

      <div class="column is-8">
        <h1 class="title">{{ article.title }}</h1>
        <h2 class="subtitle">{{ article.description }}</h2>
      </div>

      <div class="column is-4 has-text-right">
        <b-field
          v-if="$auth.check(['admin', 'support'])"
          class="has-margin-bottom-sm"
          position="is-right">
          <p class="control">
            <router-link
              :to="{ name: 'edit-article', params: { article: article.id } }"
              class="button is-primary">
              <b-icon icon="pencil-alt" size="is-small" />
              <span v-t="'edit_button'" />
            </router-link>
          </p>
          <p class="control">
            <button
              class="button is-danger"
              @click.prevent="destroy($event, article.id)">
              <b-icon icon="times" size="is-small" />
              <span v-t="'delete_button'" />
            </button>
          </p>
        </b-field>

        <time class="has-text-grey" :datetime="article.updated_at">
          {{ $t('last_revision', { date: lastUpdate }) }}
        </time>
      </div>

    </div>

    <div ref="content" class="content article-content" v-html="article.content" />
    <article-feedback :article="article.id" />

    <!-- Visualizacao ampliada: no artigo a imagem e limitada a largura da
         coluna, o que torna screenshots com texto pequeno ilegiveis. Aqui ela
         aparece no tamanho original, com rolagem quando nao couber na tela. -->
    <b-modal
      :active.sync="zoom.active"
      :can-cancel="['escape', 'outside']"
      scroll="keep"
      class="image-zoom-modal">
      <!-- Botao proprio em vez do `x` do Buefy: aquele fica fixo no canto da
           janela, longe da imagem. Este fica ao lado da borda superior direita
           da imagem, sobre o fundo escuro — fora do conteudo, onde nao se
           confunde com o que esta no screenshot. -->
      <!-- X em texto, nao `b-icon`: o projeto nao carrega o Font Awesome que o
           Buefy espera, entao os icones nao renderizam. -->
      <button
        ref="zoomClose"
        type="button"
        class="image-zoom-modal__close"
        :aria-label="$t('close_button')"
        @click="zoom.active = false">&times;</button>
      <div ref="zoomViewport" class="image-zoom-modal__viewport">
        <img
          v-if="zoom.src"
          :src="zoom.src"
          :alt="zoom.alt"
          @load="centerZoomScroll">
      </div>
    </b-modal>

  </article>
  <loading-content v-else />
</template>

<script>
import API from '@/api'
import Utils from '@/utils'
import { mapMutations } from 'vuex'
import LoadingContent from './LoadingContent'
import ArticleFeedback from './ArticleFeedback'

export default {
  name: 'ArticleContent',
  props: { product: { type: Object, required: true } },
  components: {
    LoadingContent,
    ArticleFeedback
  },
  data () {
    return {
      isLoading: true,
      pageTitle: '',
      article: null,
      zoom: {
        active: false,
        src: null,
        alt: ''
      }
    }
  },
  mounted () {
    this.fetchData()
  },
  beforeDestroy () {
    this.unbindImageZoom()
  },
  watch: {
    articleSlug () {
      this.article = null
      this.fetchData()
    }
  },
  methods: {
    fetchData () {
      this.isLoading = true
      API.articles.findBySlug(this.product.id, this.articleSlug, article => {
        if (article === null) {
          this.$router.push({ name: '404-error' })
        }

        // Define o título da página
        this.pageTitle = article.title + ' | ' + article.product.name

        this.article = article
        this.isLoading = false

        this.$nextTick(this.bindImageZoom)
      })
    },

    // O conteudo vem da API e e injetado por v-html, entao nao passa pelo
    // compilador do Vue e nao aceita @click no template. Os listeners sao
    // ligados a cada renderizacao do artigo.
    bindImageZoom () {
      this.unbindImageZoom()

      const container = this.$refs.content
      if (!container) return

      this.zoomTargets = Array.from(container.querySelectorAll('img'))
      this.zoomTargets.forEach(image => {
        image.classList.add('is-zoomable')
        image.addEventListener('click', this.openZoom)
      })
    },

    unbindImageZoom () {
      if (!this.zoomTargets) return

      this.zoomTargets.forEach(image => {
        image.removeEventListener('click', this.openZoom)
      })
      this.zoomTargets = null
    },

    openZoom (event) {
      const image = event.currentTarget

      this.zoom.src = image.getAttribute('src')
      this.zoom.alt = image.getAttribute('alt') || ''
      this.zoom.active = true
    },

    // Uma imagem maior que a tela abre com a rolagem no canto superior
    // esquerdo, mostrando so a borda. Centralizar deixa o miolo do screenshot
    // a vista, que e o que interessa na maioria dos casos.
    centerZoomScroll () {
      this.$nextTick(() => {
        const viewport = this.$refs.zoomViewport
        if (!viewport) return

        const container = viewport.parentElement || viewport

        container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2
        container.scrollTop = (container.scrollHeight - container.clientHeight) / 2

        // O botao fica oculto ate a transicao de abertura do modal (200ms,
        // com scale) terminar. Posicionar antes disso mede o `.modal-content`
        // ainda distorcido pelo scale(), e o X aparece num lugar errado para
        // so entao "pular" pra posicao certa quando a animacao assenta — o
        // que aconteceu quando essa posicao era calculada duas vezes (uma
        // logo no load da imagem, outra depois). Agora e calculada uma unica
        // vez, so depois da animacao, e o botao so aparece nesse momento.
        const button = this.$refs.zoomClose
        if (button) button.style.visibility = 'hidden'

        setTimeout(() => this.placeZoomClose(container), 220)
      })
    },

    // O botao e `fixed` para nao rolar junto com a imagem. Fica ao LADO da
    // area de visualizacao, junto a borda superior direita — fora do
    // conteudo, sobre o fundo escuro. Se nao couber ao lado (imagem larga),
    // volta para dentro da area para nao sair da tela.
    placeZoomClose (container) {
      const button = this.$refs.zoomClose
      if (!button) return

      const bounds = container.getBoundingClientRect()
      const size = button.offsetWidth || 40
      const gap = 4

      const beside = bounds.right + gap
      const left = beside + size <= window.innerWidth
        ? beside
        : bounds.right - size - gap

      button.style.top = Math.round(bounds.top) + 'px'
      button.style.left = Math.round(left) + 'px'
      button.style.visibility = 'visible'
      button.style.right = 'auto'
    },

    // Remove o artigo com base em sua ID
    destroy (event, id) {
      Utils.alertConfirmation(
        this.$t('delete_alert'),
        toast => {
          API.articles.destroy(id, () => {
            this.$snotify.remove(toast.id)
            this.$router.push({ name: 'manual', params: { product: this.product.slug } })
            this.removeArticleFromIndex(id)
          })
        },
        toast => this.$snotify.remove(toast.id),
        this.$snotify
      )
    },
    ...mapMutations({ removeArticleFromIndex: 'article/removeArticle' })
  },
  computed: {
    articleSlug () {
      return this.$route.params.article
    },
    lastUpdate () {
      return Utils.timeAgo(this.article.updated_at, this.$i18n.locale)
    }
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  }
}
</script>

<style lang="scss">
.article-content img.is-zoomable {
  cursor: zoom-in;
}

.image-zoom-modal {
  // O projeto usa o Buefy só pelos componentes JS (o CSS dele nunca é
  // importado — ver vuecomponents.js), então a transição "zoom-out" que a
  // `<transition>` interna do b-modal aplica não tinha nenhuma regra por
  // trás: abria/fechava sem transição nenhuma. Replicada aqui (baseada na
  // animação nativa do Buefy), só para este modal.
  &.zoom-out-enter-active,
  &.zoom-out-leave-active {
    transition: opacity 200ms ease-out;

    .modal-content {
      transition: transform 200ms ease-out;
    }
  }

  &.zoom-out-enter,
  &.zoom-out-leave-to {
    opacity: 0;

    .modal-content {
      transform: scale(1.05);
    }
  }

  .modal-content {
    // O Buefy sempre injeta um `style="max-width: <width prop>"` inline no
    // `.modal-content` (default 960px, ver Modal.vue de @buefy) — nao ha como
    // desativar isso via prop sem usar `full-screen`. Estilo inline vence
    // qualquer regra de stylesheet, entao o `!important` e necessario aqui
    // para a imagem poder usar o espaco todo da viewport, e nao ficar presa
    // em 960px mesmo quando menor que isso.
    position: relative;
    width: auto;
    max-width: 92vw !important;
    max-height: 92vh;
    overflow: auto;
  }

  // O `x` padrao do Buefy fica fixo no canto da viewport; usamos um botao
  // ancorado a propria imagem.
  .modal-close {
    display: none;
  }

  &__viewport {
    // Sem max-width na imagem: ela fica no tamanho original e o container
    // rola quando nao couber na tela.
    position: relative;
    display: inline-block;
    background-color: #fff;
    line-height: 0;

    img {
      max-width: none;
    }
  }

  &__close {
    // Fixo em relacao a area de visualizacao (nao a imagem, que rola por
    // dentro). A posicao exata e calculada em JS: fica a direita da imagem,
    // sobre o fundo escuro do overlay. Sem fundo nem borda — so o X.
    // Comeca oculto: so fica visible quando o JS calcula a posicao definitiva
    // (depois da transicao de abertura), pra nao aparecer no lugar errado e
    // "pular" para o lugar certo.
    visibility: hidden;
    position: fixed;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    border: none;
    background: none;
    color: #fff;
    font-size: 2.25rem;
    font-weight: 300;
    line-height: 1;
    cursor: pointer;
    opacity: 0.85;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    transition: opacity 150ms ease-out;

    &:hover,
    &:focus {
      opacity: 1;
      outline: none;
    }
  }
}
</style>

<i18n>
{
  "en-US": {
    "last_revision": "Last revision {date}",
    "edit_button": "Edit",
    "delete_button": "Remove",
    "close_button": "Close",
    "delete_alert": "Are you sure you want to remove this article?"
  },
  "pt-BR": {
    "last_revision": "Última revisão {date}",
    "edit_button": "Editar",
    "delete_button": "Remover",
    "close_button": "Fechar",
    "delete_alert": "Tem certeza que deseja remover este artigo?"
  }
}
</i18n>
